// The two answers a notification row can carry inline (ART-159, C-Bell): a friend request, and an
// opportunity invite. The ENDPOINTS are unchanged — this is a second door onto them, not a second authority.
//
// **CONTEXT-FREE, and that is the whole reason these exist rather than reusing `useRespondFriendRequest` /
// `useRespondOpportunity`.** Both of those call `useBandContext()`, which THROWS with no mounted
// BandProvider — and the bell opens from the TOPBAR, which sits outside every one of them. A hook throws
// whether or not its query is enabled, so importing either into a notification row takes the whole drawer
// down the moment it renders. (The identical defect took the inbox's picker down on 2026-09-11; it is
// pinned in `no-band-mounted.guard.test.tsx`.)
//
// **The band comes from the NOTIFICATION, never from what is mounted.** A friend request for The Wren while
// you are looking at Midnight Freight must be accepted on The Wren — and under the roster shell there is no
// mounted band to be wrong about in the first place.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApp } from "../appContext.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

/** What a row can be answered with. Both endpoints take their own vocabulary; this is the row's. */
export type NotificationAnswer = "accept" | "decline";

/**
 * `POST /bands/{bandId}/friends/requests/{friendshipId}/respond` — from a notification row.
 *
 * Invalidates the friends list for the band it acted on AND the notification keys, because the server
 * clears the pending request for every AV member who received it: the row this was pressed on is about to
 * disappear for several people, and the one in front of you should go first.
 */
export function useAnswerFriendRequest() {
  const { api } = useApp();
  const qc = useQueryClient();

  return useMutation<void, Error, { bandId: string; friendshipId: string; answer: NotificationAnswer }>({
    mutationFn: async ({ bandId, friendshipId, answer }) => {
      const { error, response } = await api.POST(
        "/api/v1/bands/{bandId}/friends/requests/{friendshipId}/respond",
        { params: { path: { bandId, friendshipId } }, body: { action: answer } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: (_v, { bandId }) => {
      void qc.invalidateQueries({ queryKey: ["band", bandId, "friends"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications", "unread"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't respond to the request")),
  });
}

/**
 * `POST /bands/{bandId}/opportunities/{oppId}/respond` — from a notification row.
 *
 * The wire vocabulary is `in` / `out` where the row's is accept / decline; translated HERE so the component
 * speaks one language for both actions and neither endpoint's spelling leaks into a button.
 */
export function useAnswerOpportunityInvite() {
  const { api } = useApp();
  const qc = useQueryClient();

  return useMutation<void, Error, { bandId: string; oppId: string; answer: NotificationAnswer }>({
    mutationFn: async ({ bandId, oppId, answer }) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/opportunities/{oppId}/respond", {
        params: { path: { bandId, oppId } },
        body: { response: answer === "accept" ? "in" : "out" },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: (_v, { bandId }) => {
      void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] });
      // The person-scoped key too: an invite is cross-band and lives outside ['band', …] so it survives a
      // band switch, exactly as notifications do.
      void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications", "unread"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't send your response")),
  });
}

/**
 * `POST /bands/{bandId}/shows/{showId}/shares/{shareId}/respond` — a SHARED-DATE invite, from a
 * notification row. The one answerable kind whose endpoint is NESTED, which is why the row carries a
 * `subjectParentId` (the show) beside its `subjectId` (the share).
 *
 * Context-free for the same reason as the two above — `useRespondShare` takes its band from
 * `useBandContext()` and its show from a prop, neither of which a bell row has.
 */
export function useAnswerShowShare() {
  const { api } = useApp();
  const qc = useQueryClient();

  return useMutation<void, Error, { bandId: string; showId: string; shareId: string; answer: NotificationAnswer }>({
    mutationFn: async ({ bandId, showId, shareId, answer }) => {
      const { error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/shares/{shareId}/respond",
        // The wire says accept/decline as a BOOLEAN here where the other two say strings — translated at
        // this seam so a button never spells an endpoint's vocabulary.
        { params: { path: { bandId, showId, shareId } }, body: { accept: answer === "accept" } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: (_v, { bandId, showId }) => {
      void qc.invalidateQueries({ queryKey: ["band", bandId, "shared-shows"] });
      void qc.invalidateQueries({ queryKey: ["band", bandId, "shared-show", showId] });
      // The show lands on (or leaves) the guest band's own schedule either way.
      void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications", "unread"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't respond to the invite")),
  });
}

/**
 * The notification kinds a row can answer INLINE, and which endpoint answers them.
 *
 * A closed table rather than a predicate, so a kind added server-side renders as an ordinary link until
 * somebody decides what answering it means — the `TAG_LABELS` rule, applied to an action instead of a
 * label. Getting this wrong in the other direction would put two buttons on a row with nothing behind them.
 */
export const ANSWERABLE_KINDS: Record<string, "friend" | "opportunity" | "share" | "seat"> = {
  friend_request: "friend",
  // ART-239: a crew / org-team / partner invite to an existing account. Answered at the PERSON-scoped
  // /me/invites/{id}, so — unlike every kind above — it needs no band on the row (an org invite has none).
  seat_invite: "seat",
  // The SHARED-DATE invite — the spec's other named case, and the only one whose respond route is nested,
  // so its row is the only one that needs `subjectParentId` as well.
  support_added: "share",
  show_opportunity: "opportunity",
  tour_opportunity: "opportunity",
  rehearsal_invite: "opportunity",
  content_invite: "opportunity",
  pr_invite: "opportunity",
  placement_invite: "opportunity",
};

/**
 * `POST /me/invites/{inviteId}/accept|decline` — a seat invite (crew, org team, partner) from a bell row (ART-239).
 * Person-scoped, so it needs no band — the one answerable kind that doesn't. Accepting mints a seat or a link, so
 * `me` is invalidated too: the new band or org has to reach the account chip and the shell selection.
 */
export function useAnswerSeatInvite() {
  const { api } = useApp();
  const qc = useQueryClient();

  return useMutation<void, Error, { inviteId: string; answer: NotificationAnswer }>({
    mutationFn: async ({ inviteId, answer }) => {
      const { error, response } =
        answer === "accept"
          ? await api.POST("/api/v1/me/invites/{inviteId}/accept", { params: { path: { inviteId } } })
          : await api.POST("/api/v1/me/invites/{inviteId}/decline", { params: { path: { inviteId } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["me"] });
      void qc.invalidateQueries({ queryKey: ["me", "invites"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications", "unread"] });
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't respond to the invite")),
  });
}
